<template>
  <div v-if="myMachines" class="my-machines-selector">
    <slot name="activator" :open="open">
      <oie-base-action
        v-tooltip.top="tooltip"
        :disabled="$attrs.disabled"
        class="my-machines-selector__activator"
        theme="outlined"
        @click="open">
        <template #iconPrepend>
          <oie-base-icon icon="machine" size="xs"></oie-base-icon>
        </template>
        {{ buttonText }}
      </oie-base-action>
    </slot>
    <oie-portal-wrapper :name="id" class="my-machines-selector__slide-in">
      <template #title>
        <h2>{{ tStatic('registeredOwnersAndMachines') }}</h2>
      </template>
      <div class="my-machines-selector__content">
        <div class="filter-container">
          <oie-base-input v-model="filter" :placeholder="tStatic('filterMachines')">
            <template #iconPrepend>
              <oie-base-icon icon="filter-icon" size="s"></oie-base-icon>
            </template>
          </oie-base-input>
          <oie-base-dropdown
            v-model="showFilter"
            :initial-text="tStatic('showOnlyMyMachinesAll')"
            :options="createShowFilterSelection"></oie-base-dropdown>
        </div>
        <oie-my-machines-list-wrapper v-if="filteredMachineOwners.length > 0">
          <template #title>
            <h2>
              {{ tStatic('machineOwnersTitle') }}
            </h2>
          </template>
          <li v-for="customer in filteredMachineOwners" :key="`customer-${customer.machineOwnerId}`">
            <oie-machine-owner-item
              :with-actions="false"
              :customer="customer"
              :show-favourite="false"
              @select-machine-owner="handleSelectMachineOwner"
              @select-machine="handleSelectMachine">
              <template #machine-actions="{ machine }">
                <oie-base-action
                  v-if="machine.sparePartsUrl"
                  v-tooltip.top="tStatic('spareParts')"
                  theme="flat"
                  :url="`${machine.sparePartsUrl}?hasComeFromMachine=${machine.machineId}`">
                  <oie-base-icon
                    icon="machine-spare-parts"
                    size="xs"></oie-base-icon>
                </oie-base-action>
                <oie-base-action
                  v-tooltip.top="tStatic('goToMachine')"
                  theme="flat"
                  @click="goToMachine(machine)">
                  <oie-base-icon
                    icon="launch"
                    size="xs"></oie-base-icon>
                </oie-base-action>
              </template>
            </oie-machine-owner-item>
          </li>
        </oie-my-machines-list-wrapper>
        <oie-my-machines-list-wrapper v-if="filteredMachines.length > 0">
          <template #title>
            <h2>
              {{ tStatic('machinesTitle') }}
            </h2>
          </template>
          <li v-for="machine in filteredMachines" :key="`machine-${machine.machineId}`">
            <oie-machine-item :machine="machine" :show-favourite="false" @select-machine="handleSelectMachine">
              <template #actions>
                <oie-base-action
                  v-if="machine.sparePartsUrl"
                  v-tooltip.top="tStatic('spareParts')"
                  theme="flat"
                  :url="`${machine.sparePartsUrl}?hasComeFromMachine=${machine.machineId}`">
                  <oie-base-icon
                    icon="machine-spare-parts"
                    size="xs"></oie-base-icon>
                </oie-base-action>
                <oie-base-action
                  v-tooltip.top="tStatic('goToMachine')"
                  theme="flat"
                  @click="goToMachine(machine)">
                  <oie-base-icon
                    icon="launch"
                    size="xs"></oie-base-icon>
                </oie-base-action>
              </template>
            </oie-machine-item>
          </li>
        </oie-my-machines-list-wrapper>
      </div>
    </oie-portal-wrapper>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { SUB_PAGES } from '@/components/new-my-pages/my-pages-container.vue'
import { showMyMachinesGdprConsentModal } from '@/core/modal-service'
import _ from 'lodash'

export default {
  props: {
    text: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters('myMachines', ['myMachines', 'myMachinesFilter', 'filteredMachineOwners', 'filteredMachines', 'myMachinesIsLocked', 'myMachinesShowFilter']),
    id() {
      return _.uniqueId('my-machines-selector-')
    },
    filter: {
      get() { return this.myMachinesFilter },
      set(val) {
        this.setMyMachinesFilter({ filter: val })
      }
    },
    showFilter: {
      get() { return this.myMachinesShowFilter },
      set(val) {
        this.setMyMachinesShowFilter({ showFilter: val })
      }
    },
    buttonText() {
      return this.text ? this.text : this.tStatic('selectMachine')
    },
    createShowFilterSelection() {
      return [
        { value: 'all', text: this.tStatic('showOnlyMyMachinesAll') },
        { value: 'fav', text: this.tStatic('showOnlyMyMachinesFavourites') },
        ...(this.clientModel.user.isMultiUser
          ? [{ value: 'mine', text: this.tStatic('showOnlyMyMachinesCreatedByMe') }]
          : [])
      ]
    }
  },
  methods: {
    ...mapActions('ui', ['setActiveSlideIn']),
    ...mapActions('myMachines', ['setMyMachinesFilter', 'setCurrentMachine', 'setMyMachinesShowFilter']),
    async open() {
      if(this.myMachinesIsLocked) {
        const consent = await showMyMachinesGdprConsentModal()
        if(consent) {
          this.setActiveSlideIn(this.id)
        }
      } else {
        this.setActiveSlideIn(this.id)
      }
    },
    handleSelectMachineOwner(owner) {
      this.$emit('select-owner', owner)
      this.setActiveSlideIn(null)
    },
    handleSelectMachine(machine) {
      this.$emit('select-machine', machine)
      this.setActiveSlideIn(null)
    },
    goToMachine(machine) {
      this.setCurrentMachine(machine)
      this.setActiveSlideIn(null)
      window.location = `${this.clientModel.myPageUrl}#${SUB_PAGES.oieMachineDetails.name}`
    }

  }
}
</script>

<style lang="scss">
.my-machines-selector__content >*:not(:last-child) {
  margin-bottom: measure(half);
}
.my-machines-selector__content .filter-container {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
